import React from "react";
import { Divider, Stack, Typography } from "@financeable-com-au/financeable-ui";
import { getAssetSearchOptionsIntro } from "@/constants";

interface AssetSearchOptionProps {
  title: string;
  children: React.ReactNode;
}

/**
 * One way of identifying the asset: its title over the fields it takes. `useFlexGap` spaces them without
 * zeroing the fields' own margins, which an MUI `Grid` with spacing needs to line up under the title.
 */
export const AssetSearchOption = ({ title, children }: AssetSearchOptionProps) => (
  <Stack spacing={3} useFlexGap>
    <Typography level="title-sm">{title}</Typography>
    {children}
  </Stack>
);

interface AssetSearchOptionsProps {
  children: React.ReactNode;
}

/**
 * Lists the search options straight on the page, split by plain dividers. The intro sentence already says any
 * one of them fills in the asset, so the dividers carry no "OR" and the options are deliberately unnumbered:
 * numbers read as steps to work through in order.
 *
 * Callers pass the options as conditional children; `React.Children.toArray` drops the ones that
 * evaluated to `false`, so the intro only counts options on screen and a divider never lands next to a hidden one.
 */
const AssetSearchOptions = ({ children }: AssetSearchOptionsProps) => {
  const options = React.Children.toArray(children);

  if (options.length === 0) return null;

  // A lone option is not a choice, so it gets no sentence telling the broker to pick.
  const intro = options.length > 1 ? getAssetSearchOptionsIntro(options.length) : null;

  return (
    <Stack spacing={4}>
      {intro && (
        <Typography level="body-sm" textColor="text.tertiary">
          {intro.LEAD}{" "}
          <Typography level="body-sm" textColor="text.primary" bold component="span">
            {intro.EMPHASIS}
          </Typography>{" "}
          {intro.TRAIL}
        </Typography>
      )}
      <Stack spacing={5} divider={<Divider />}>
        {options}
      </Stack>
    </Stack>
  );
};

export default AssetSearchOptions;
